import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';
import { IVideoClip, IEpisode, VideoEditConfig, IVideoClipResource } from '@packages/global';
import { usePlayback } from './usePlayback.ts';
import { useBgmPlayer } from './useBgmPlayer.ts';

interface VideoEditorState {
  // 播放状态
  isPlaying: boolean;
  currentTime: number;
  currentPlaybackInfo: {
    clip: IVideoClip | null;
    localTime: number;
    trimStart: number;
    trimEnd: number;
  } | null;

  // 片段状态
  selectedClip: IVideoClip | null;
  selectedClipView: 'video' | 'audio' | 'text' | null; // 新增：当前激活的视图
  isBgmTrackActive: boolean; // 新增：背景音乐轨道是否激活

  // 编辑配置
  editConfig: VideoEditConfig | null;

  // UI 状态
  activeTab: 'preview' | 'images' | 'audio' | 'video' | 'bgm' | 'effects';
  subtitleEnabled: boolean;
  previewVideoTime: number | null; // 新增：手动设置的预览视频时间

  // 数据
  videoClips: IVideoClip[];
  episode: IEpisode | null;

  // 资源轮询状态
  resourceStatusMap: Map<string, IVideoClipResource[]>;
  pendingClipIds: Set<string>;
  isResourcePolling: boolean;
}

interface VideoEditorActions {
  // 播放控制
  play: () => void;
  pause: () => void;
  togglePlay: () => void;
  seek: (time: number) => void;
  setPreviewTime: (time: number) => void; // 新增：直接设置预览视频时间

  // 片段操作
  selectClip: (clip: IVideoClip | null, view?: 'video' | 'audio' | 'text') => void;
  updateEditConfig: (config: VideoEditConfig) => void;
  saveEditConfigImmediately: (config: VideoEditConfig) => Promise<void>; // 新增：立即保存
  toggleBgmTrack: () => void; // 新增：切换背景音乐轨道激活状态

  // UI 操作
  setActiveTab: (tab: 'preview' | 'images' | 'audio' | 'video' | 'bgm' | 'effects') => void;
  toggleSubtitle: () => void;

  // 资源轮询操作
  triggerResourcePoll: () => Promise<void>;
  getClipResources: (clipId: string) => IVideoClipResource[];
  isClipPending: (clipId: string) => boolean;
  updateResourceStatus: (clipId: string, resources: IVideoClipResource[]) => void;
}

interface VideoEditorContextValue {
  state: VideoEditorState;
  actions: VideoEditorActions;
}

const VideoEditorContext = createContext<VideoEditorContextValue | null>(null);

// 稳定的空数组引用：避免 getClipResources 在无资源时每次返回新数组，
// 否则会让依赖 resources 的下游（如无限画布 context value）在每次渲染都变化。
const EMPTY_RESOURCES: IVideoClipResource[] = [];

interface VideoEditorProviderProps {
  children: React.ReactNode;
  videoClips: IVideoClip[];
  episode: IEpisode | null;
  editConfig: VideoEditConfig | null;
  onEditConfigChange: (config: VideoEditConfig) => void;
  onSaveEditConfigImmediately?: (config: VideoEditConfig) => Promise<void>; // 新增：立即保存回调
  initialSelectedClip?: IVideoClip | null;
  // 资源轮询相关
  resourceStatusMap?: Map<string, IVideoClipResource[]>;
  pendingClipIds?: Set<string>;
  isResourcePolling?: boolean;
  onTriggerResourcePoll?: () => Promise<void>;
  onResourceStatusUpdate?: (clipId: string, resources: IVideoClipResource[]) => void;
}

export function VideoEditorProvider({
  children,
  videoClips,
  episode,
  editConfig,
  onEditConfigChange,
  onSaveEditConfigImmediately,
  initialSelectedClip = null,
  resourceStatusMap = new Map(),
  pendingClipIds = new Set(),
  isResourcePolling = false,
  onTriggerResourcePoll,
  onResourceStatusUpdate
}: VideoEditorProviderProps) {
  // UI 状态
  const [selectedClip, setSelectedClip] = useState<IVideoClip | null>(initialSelectedClip);
  const [selectedClipView, setSelectedClipView] = useState<'video' | 'audio' | 'text' | null>(null);

  // 当 videoClips 更新时，同步刷新 selectedClip（保证预览窗口实时反映最新数据）
  useEffect(() => {
    if (selectedClip) {
      const updated = videoClips.find(c => c.id === selectedClip.id);
      if (updated && updated !== selectedClip) {
        setSelectedClip(updated);
      }
    }
  }, [videoClips]);
  const [isBgmTrackActive, setIsBgmTrackActive] = useState(false);
  const [activeTab, setActiveTab] = useState<'preview' | 'images' | 'audio' | 'video' | 'bgm' | 'effects'>('preview');
  const [subtitleEnabled, setSubtitleEnabled] = useState(true);
  const [previewVideoTime, setPreviewVideoTime] = useState<number | null>(null); // 新增：预览视频时间

  // 使用播放 hook
  const playback = usePlayback(videoClips, editConfig);

  // 使用 BGM 播放 hook（在 Context 层面管理）
  const bgmPlayer = useBgmPlayer({
    episode,
    editConfig,
    isPlaying: playback.isPlaying,
    currentTime: playback.currentTime
  });

  // 定义 action 函数（在 useMemo 外部）
  const selectClip = useCallback((clip: IVideoClip | null, view?: 'video' | 'audio' | 'text') => {
    setSelectedClip(clip);

    // 设置视图：如果提供了 view 参数则使用，否则默认为 video
    if (clip) {
      const newView = view || 'video';
      setSelectedClipView(newView);
    } else {
      setSelectedClipView(null);
    }

    // 非播放状态才 seek，播放时不打断播放位置
    if (!playback.isPlaying && clip && editConfig) {
      const clipInfo = editConfig.clips.find(c => c.clipId === clip.id);
      if (clipInfo) {
        playback.seek(clipInfo.startTime);
      }
    }
  }, [playback, editConfig]);

  const updateEditConfig = useCallback((config: VideoEditConfig) => {
    onEditConfigChange(config);
  }, [onEditConfigChange]);

  const saveEditConfigImmediately = useCallback(async (config: VideoEditConfig) => {
    onEditConfigChange(config);
    if (onSaveEditConfigImmediately) {
      await onSaveEditConfigImmediately(config);
    }
  }, [onEditConfigChange, onSaveEditConfigImmediately]);

  const setActiveTabAction = useCallback((tab: 'preview' | 'images' | 'audio' | 'video' | 'bgm' | 'effects') => {
    setActiveTab(tab);
    // 如果手动切换到非预览tab且正在播放，则停止播放
    if (tab !== 'preview' && playback.isPlaying) {
      playback.pause();
    }
  }, [playback]);

  const toggleSubtitle = useCallback(() => {
    setSubtitleEnabled(prev => !prev);
  }, []);

  const toggleBgmTrack = useCallback(() => {
    setIsBgmTrackActive(prev => !prev);
  }, []);

  const setPreviewTime = useCallback((time: number) => {
    setPreviewVideoTime(time);
  }, []);

  const play = useCallback(() => {
    setActiveTab('preview'); // 播放时强制切换到预览标签
    setPreviewVideoTime(null); // 清除手动设置的时间
    playback.play();
  }, [playback]);

  const togglePlay = useCallback(() => {
    if (playback.isPlaying) {
      playback.pause();
    } else {
      setActiveTab('preview'); // 播放时强制切换到预览标签
      setPreviewVideoTime(null); // 清除手动设置的时间
      playback.play();
    }
  }, [playback]);

  // 资源轮询相关 actions
  const triggerResourcePoll = useCallback(async () => {
    if (onTriggerResourcePoll) {
      await onTriggerResourcePoll();
    }
  }, [onTriggerResourcePoll]);

  const getClipResources = useCallback((clipId: string): IVideoClipResource[] => {
    return resourceStatusMap.get(clipId) || EMPTY_RESOURCES;
  }, [resourceStatusMap]);

  const isClipPending = useCallback((clipId: string): boolean => {
    return pendingClipIds.has(clipId);
  }, [pendingClipIds]);

  const updateResourceStatus = useCallback((clipId: string, resources: IVideoClipResource[]) => {
    if (onResourceStatusUpdate) {
      onResourceStatusUpdate(clipId, resources);
    }
  }, [onResourceStatusUpdate]);

  // Actions
  const actions: VideoEditorActions = useMemo(() => ({
    play,
    pause: playback.pause,
    togglePlay,
    seek: playback.seek,
    setPreviewTime,
    selectClip,
    updateEditConfig,
    saveEditConfigImmediately,
    toggleBgmTrack,
    setActiveTab: setActiveTabAction,
    toggleSubtitle,
    triggerResourcePoll,
    getClipResources,
    isClipPending,
    updateResourceStatus
  }), [play, playback.pause, togglePlay, playback.seek, setPreviewTime, selectClip, updateEditConfig, saveEditConfigImmediately, toggleBgmTrack, setActiveTabAction, toggleSubtitle, triggerResourcePoll, getClipResources, isClipPending, updateResourceStatus]);

  // State
  const state: VideoEditorState = useMemo(() => ({
    isPlaying: playback.isPlaying,
    currentTime: playback.currentTime,
    currentPlaybackInfo: playback.currentPlaybackInfo,
    selectedClip,
    selectedClipView,
    isBgmTrackActive,
    editConfig,
    activeTab,
    subtitleEnabled,
    videoClips,
    episode,
    previewVideoTime,
    resourceStatusMap,
    pendingClipIds,
    isResourcePolling
  }), [
    playback.isPlaying,
    playback.currentTime,
    playback.currentPlaybackInfo,
    selectedClip,
    selectedClipView,
    isBgmTrackActive,
    editConfig,
    activeTab,
    subtitleEnabled,
    videoClips,
    episode,
    previewVideoTime,
    resourceStatusMap,
    pendingClipIds,
    isResourcePolling
  ]);

  const value = useMemo(() => ({ state, actions }), [state, actions]);

  return (
    <VideoEditorContext.Provider value={value}>
      {children}
    </VideoEditorContext.Provider>
  );
}

export function useVideoEditor() {
  const context = useContext(VideoEditorContext);
  if (!context) {
    throw new Error('useVideoEditor must be used within VideoEditorProvider');
  }
  return context;
}
